/**
 * Session Timeout Hook
 * Automatically logs out users after a period of inactivity
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePlatformSettings } from '../contexts/PlatformSettingsContext';

// Default timeout: 60 minutes (in milliseconds)
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000;

// Warning before logout: 2 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;

interface UseSessionTimeoutOptions {
  onWarning?: (remainingMs: number) => void;
  onTimeout?: () => void;
}

export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const { isAuthenticated, logout } = useAuthStore();
  const { settings } = usePlatformSettings();
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  
  // Get timeout from platform settings or use default
  const timeoutMs = (settings?.session_timeout_minutes ?? 60) * 60 * 1000;
  
  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    
    if (!isAuthenticated) return;
    
    // Set warning timer
    warningRef.current = setTimeout(() => {
      if (options.onWarning) {
        options.onWarning(WARNING_BEFORE_MS);
      }
    }, timeoutMs - WARNING_BEFORE_MS);
    
    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      if (options.onTimeout) {
        options.onTimeout();
      }
      logout();
    }, timeoutMs);
  }, [isAuthenticated, logout, timeoutMs, options]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers when logged out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      return;
    }
    
    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];
    
    // Debounce activity updates
    let activityTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
        resetTimeout();
      }, 1000); // Only reset every second at most
    };
    
    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Initial timeout setup
    resetTimeout();
    
    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [isAuthenticated, resetTimeout]);
  
  return {
    resetTimeout,
    lastActivity: lastActivityRef.current,
  };
}


